import { useState, useEffect, useRef, useCallback } from 'react';

interface UseLocationInputProps {
  value: string;
  onChange: (value: string) => void;
  recentLocations: string[];
  /** Fired when the user picks an option from the list (not while typing). */
  onSelect?: (value: string) => void;
  /**
   * Fired when the user is done with this field — a selection, or leaving it
   * with something typed. Lets the caller tell "this is an address" from
   * "this is half an address being typed", which is the difference between
   * showing a route and showing a route error.
   */
  onCommit?: (value: string) => void;
}

export interface LocationSuggestion {
  text: string;
  place_name: string;
  category?: string;
}

export const useLocationInput = ({
  value,
  onChange,
  recentLocations,
  onSelect,
  onCommit,
}: UseLocationInputProps) => {
  // Internal state for the input field
  const [inputValue, setInputValue] = useState(value);
  // Track if the input is focused
  const [isFocused, setIsFocused] = useState(false);
  // Track if popover should be shown
  const [showPopover, setShowPopover] = useState(false);
  // Ref for input element for direct manipulation
  const inputRef = useRef<HTMLInputElement>(null);
  // The field wrapper: input, buttons and the suggestion list. Anything outside
  // it is "outside" for dismissal purposes.
  const containerRef = useRef<HTMLDivElement>(null);
  // Track if this is initial render
  const isInitialMount = useRef(true);
  // The last value this field exchanged with the parent — pushed up, or
  // accepted down. Guards the echo described on the notify effect below.
  const lastSyncedRef = useRef(value);
  
  
  // Sync internal input value when prop value changes. Uses the functional
  // updater so this doesn't need `inputValue` in the dependency array —
  // depending on it directly would re-run on every keystroke and fight the
  // user's typing by resetting back to the (stale) prop value.
  useEffect(() => {
    lastSyncedRef.current = value;
    setInputValue((prev) => (prev === value ? prev : value));
  }, [value]);
  
  // Tell the parent as the user types, with no delay of our own.
  //
  // There was a 300ms debounce here *and* a 500ms one in useLocationInputs on
  // the value this pushes, and they are sequential: 800ms elapsed before the
  // geocoding request was even issued, against a 400ms bar (PRODUCTION_BAR.md
  // §2, Doherty Threshold). One debounce, owned by the code that actually
  // makes the network call, is the whole fix.
  //
  // The guard stays, and must not fire because `value` changed: `value` was
  // once in the deps with the test `debounced !== value`, so the moment the
  // parent's value moved — choosing a suggestion sets it at once — this re-ran
  // holding the *previous* text, found they differed, and pushed that stale
  // text back over the selection. Comparing against what we last exchanged
  // with the parent tells "the user typed" from "it changed underneath us".
  // See docs/FAILURE_PATTERNS.md #13.
  useEffect(() => {
    // Skip the first render to avoid unnecessary updates
    if (isInitialMount.current) {
      isInitialMount.current = false;
      lastSyncedRef.current = inputValue;
      return;
    }

    if (inputValue === lastSyncedRef.current) return;

    lastSyncedRef.current = inputValue;
    onChange(inputValue);
  }, [inputValue, onChange]);
  
  // Whether the list has anything worth showing for this input.
  //
  // `focused` is a parameter rather than a read of `isFocused` from the
  // closure: handleFocus calls this in the same tick as setIsFocused(true), so
  // the closure still holds `false` and an empty field with recent locations
  // never opened until the user clicked away and back.
  const determinePopoverVisibility = useCallback((inputVal: string, focused: boolean) => {
    // Show the list if the query is long enough to search on, or if we have
    // recent locations to offer (for an empty field, only while focused).
    return (
      (inputVal.length >= 2) ||
      (recentLocations.length > 0 && (inputVal.length === 0 ? focused : true))
    );
  }, [recentLocations.length]);
  
  // Handle text input change with optimized logic
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // Update internal state immediately for responsive UI
    setInputValue(newValue);

    // Typing implies focus.
    setShowPopover(determinePopoverVisibility(newValue, true));
  }, [determinePopoverVisibility]);
  
  // Clear the input with optimized focus handling
  const handleClear = useCallback(() => {
    setInputValue('');
    // Keep focus on input after clearing for better UX
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      // If we have recent locations, show them after clearing
      setShowPopover(recentLocations.length > 0);
    });
  }, [recentLocations.length]);
  
  // Handle input focus with optimized popover logic
  const handleFocus = useCallback(() => {
    setIsFocused(true);
    setShowPopover(determinePopoverVisibility(inputValue, true));
  }, [inputValue, determinePopoverVisibility]);
  
  // Blur deliberately does NOT close the list. Clicking a suggestion blurs the
  // input first, and closing here would unmount the option between mousedown
  // and mouseup so the click never lands. Radix already distinguishes the two
  // cases for us — a click inside the content is not an interact-outside — and
  // reports the real ones through handlePopoverOpenChange below.
  const handleBlur = useCallback(() => {
    setIsFocused(false);
    onCommit?.(inputValue);
  }, [onCommit, inputValue]);
  
  // Optimized keyboard event handler
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      // Hide popover and remove focus on escape
      setShowPopover(false);
      inputRef.current?.blur();
    }
  }, []);
  
  // Dismiss when the user interacts anywhere outside this field.
  //
  // This is ours rather than Radix's because the list is no longer a Popover.
  // Radix's DismissableLayer is built for something that owns focus; a combobox
  // must leave focus in the input, which it reads as "focus went outside" and
  // answers by closing itself ~200ms after opening. Guarding onFocusOutside,
  // then onInteractOutside, then both, each failed in turn — the primitive was
  // the wrong shape, not the guard. Owning ~15 lines of dismissal is simpler
  // than fighting it, and it is why the list both stays open while typing and
  // actually closes on demand.
  //
  // `pointerdown` rather than `click`: a click that starts inside the list and
  // ends outside it should not dismiss mid-interaction.
  useEffect(() => {
    if (!showPopover) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && containerRef.current?.contains(target)) return;
      setShowPopover(false);
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [showPopover]);
  
  // Handle suggestion selection with optimized flow
  const handleSelectLocation = useCallback((location: string) => {
    setInputValue(location);
    // Record it as exchanged so the debounce, which is still carrying the
    // half-typed query, does not push that back over the selection.
    lastSyncedRef.current = location;
    setShowPopover(false);
    onSelect?.(location);
    onCommit?.(location);
    // Schedule blur to happen after state updates
    requestAnimationFrame(() => {
      inputRef.current?.blur();
    });
  }, [onSelect, onCommit]);

  return {
    inputValue,
    isFocused,
    showPopover,
    inputRef,
    containerRef,
    isInitialMount,
    handleInputChange,
    handleClear,
    handleFocus,
    handleBlur,
    handleKeyDown,
    handleSelectLocation,
    setShowPopover
  };
};
